import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import { zodResolver } from "@hookform/resolvers/zod";
import { orderBy } from "firebase/firestore";
import React, { useMemo } from "react";
import {
    Badge,
    Button,
    Card,
    Col,
    Form,
    ListGroup,
    Row,
    Spinner,
} from "react-bootstrap";
import { useController, useFieldArray, useForm } from "react-hook-form";
import { BsExclamationCircle } from "react-icons/bs";
import {
    FaClone,
    FaExclamationTriangle,
    FaGripVertical,
    FaPlus,
    FaSave,
    FaTrash,
} from "react-icons/fa";
import { useLocation, useNavigate, useParams } from "react-router";
import { generate } from "short-uuid";
import { z } from "zod";

import {
    TaskDocument,
    useAppCollection,
    useAreasDocument,
    useLocationDocument,
    useTaskDocument,
} from "../../api/firestoreWrappers.generated.jsx";
import { Breadcrumbs, breadcrumbPaths } from "../../components/Breadcrumbs";
import CancelButton from "../../components/CancelButton/index.jsx";
import {
    canEditTaskWithCommands,
    canUseCommand,
    commandInfo,
} from "../../components/Commands/index.jsx";
import InlineFormInput from "../../components/FormGroups/InlineFormInput.jsx";
import Layout from "../../components/Layout/Layout";
import { useUser } from "../../contexts/user_provider";
import urls from "../../urls.jsx";

function generateCommandTypeOptions(
    selectedCommandType,
    userIsAdmin,
    showAdvanced,
    resourceOrganizationId,
) {
    return Object.entries(commandInfo)
        .filter(([type, info]) => {
            // If the command is already selected, don't filter it out
            if (type === selectedCommandType) {
                return true;
            }

            // Filter out admin-only commands which can't be selected by the user
            if (!canUseCommand(info, userIsAdmin, resourceOrganizationId)) {
                return false;
            }

            // Filter out advanced commands if showAdvanced is false
            if (!showAdvanced && info.isAdvanced) {
                return false;
            }
            return true;
        })
        .map(([type, info]) => ({
            value: type,
            label: info.label,
            isAdvanced: info.isAdvanced,
        }));
}

function CommandEdit({
    command,
    commandIdx,
    areas,
    control,
    watch,
    clearErrors,
    errors,
    update,
    userIsAdmin,
    showAdvancedCommandTypes,
    resourceOrganizationId,
}) {
    const selectedCommandType = useController({
        name: `commands.${commandIdx}.type`,
        control,
        defaultValue: command.type || "SCHEDULE_MOVE_KAP",
    });

    // Determine command type options and include currently selected command type
    const commandTypeOptions = generateCommandTypeOptions(
        selectedCommandType.field.value,
        userIsAdmin,
        showAdvancedCommandTypes,
        resourceOrganizationId,
    );

    // Handle command type change
    const handleCommandTypeChange = (newCommandType) => {
        // Get a skeleton version of the new command type
        const commandSkeleton = commandInfo[newCommandType]?.Skeleton;

        if (!commandSkeleton) {
            console.error(
                `No command skeleton found for type: ${newCommandType}`,
            );
            return;
        }

        // Clear errors for the current command (before updating)
        clearErrors(`commands.${commandIdx}`);

        // Clone the skeleton to avoid mutating the original structure
        const updatedCommand = { ...commandSkeleton };

        // Update the command at commandIdx
        update(commandIdx, updatedCommand);
    };

    const EditComponent = commandInfo[selectedCommandType.field.value]?.Edit;

    return (
        <>
            <div className="mt-2 me-2">
                <strong>{commandIdx + 1}.</strong>
            </div>
            <div className="flex-grow-1">
                <div className="d-inline-flex flex-wrap align-items-flex-start">
                    {commandTypeOptions.length > 1 ? (
                        <InlineFormInput
                            name={`commands.${commandIdx}.type`}
                            control={control}
                            defaultValue={command?.type || ""}
                            errors={errors?.commands?.[commandIdx]?.type}
                            placeholder="Command type"
                            inputType="select"
                            options={commandTypeOptions}
                            formatSelectOptionLabel={(option) => (
                                <>
                                    {option.isAdvanced && (
                                        <FaExclamationTriangle
                                            color="orange"
                                            className="me-2"
                                        />
                                    )}
                                    {option.label}
                                </>
                            )}
                            field={selectedCommandType.field}
                            onChange={handleCommandTypeChange}
                        />
                    ) : (
                        <span className="my-2 me-2">
                            {commandInfo[selectedCommandType.field.value].label}
                        </span>
                    )}

                    {EditComponent && (
                        <>
                            <div className="w-100 d-block d-sm-none"></div>
                            <EditComponent
                                command={command}
                                commandIdx={commandIdx}
                                areas={areas}
                                control={control}
                                watch={watch}
                                errors={errors}
                            />
                        </>
                    )}
                </div>
            </div>
        </>
    );
}

export default function TaskEditPage() {
    const { organizationId, locationId, taskId } = useParams();
    const { getUserIsAdmin } = useUser();

    const [allApps, isLoadingAppDoc] = useAppCollection([
        orderBy("name", "asc"),
    ]);
    const [locationDoc, isLoadingLocationDoc] = useLocationDocument(
        organizationId,
        locationId,
    );
    const isLoadingApps = isLoadingAppDoc || isLoadingLocationDoc;

    const installedApps = useMemo(() => {
        const locationAppIds = (locationDoc?.apps || []).map((a) => a.id);
        return allApps.filter((a) => locationAppIds.includes(a.id));
    }, [allApps, locationDoc?.apps]);

    const [areasDoc] = useAreasDocument(organizationId, locationId, {
        mode: "once",
    });
    const areas = useMemo(() => areasDoc?.areas || {}, [areasDoc?.areas]);
    const [taskSchema, setTaskSchema] = React.useState(null);
    const [existingAppSettings, setExistingAppSettings] = React.useState({});
    const [showAdvancedCommandTypes, setShowAdvancedCommandTypes] =
        React.useState(false);
    const [isLoadingTask, setIsLoadingTask] = React.useState(false);
    const [hasFormBeenTouched, setHasFormBeenTouched] = React.useState(false);
    const resolver = React.useMemo(
        () => (taskSchema ? zodResolver(taskSchema) : undefined),
        [taskSchema],
    );
    const location = useLocation();
    const [task] = useTaskDocument(organizationId, locationId, taskId);

    const navigate = useNavigate();

    const {
        control,
        handleSubmit,
        reset,
        watch,
        clearErrors,
        setValue,
        formState: { errors, isDirty, isSubmitting },
    } = useForm({ resolver });

    // useFieldArray to handle the list of commands
    const {
        fields: commandFields,
        move: moveCommand,
        append: appendCommand,
        remove: removeCommand,
        update: updateCommand,
    } = useFieldArray({
        control,
        name: "commands",
    });

    const {
        fields: appFields,
        append: appendApp,
        remove: removeApp,
    } = useFieldArray({
        control,
        name: "apps",
        keyName: "fieldId", // use this as key to prevent collision with app.id
    });

    const nameController = useController({
        name: `name`,
        control,
        defaultValue: "",
    });

    const autoNameEnabledController = useController({
        name: `autoName.enabled`,
        control,
        defaultValue: false,
    });

    const watchedCommands = watch(`commands`);
    const watchedApps = watch(`apps`);
    const watchedAutoNameEnabled = watch(`autoName.enabled`);

    const isNewTask = React.useCallback(() => {
        return taskId === undefined;
    }, [taskId]);

    React.useEffect(() => {
        if (isDirty && !hasFormBeenTouched) {
            setHasFormBeenTouched(true);
        }
    }, [isDirty, hasFormBeenTouched]);

    React.useEffect(() => {
        // Once areas are loaded, set the task schema
        if (areas) {
            const commandSchemas = Object.values(commandInfo).map((command) =>
                command.Schema(areas),
            );

            const commandItemSchema = z.discriminatedUnion(
                "type",
                commandSchemas,
            );

            setTaskSchema(
                z.object({
                    id: z.string(),
                    name: z.string().min(1, "Enter a name"),
                    autoName: z.object({
                        enabled: z.boolean(),
                    }),
                    commands: z
                        .array(commandItemSchema)
                        .nonempty("Add at least 1 command")
                        .refine(
                            (commands) => {
                                const hasSetPayloadRecording = commands.some(
                                    (command) =>
                                        command.type ===
                                        "SCHEDULE_SET_PAYLOAD_RECORDING",
                                );
                                const hasSetPictureDistanceOrMoveKap =
                                    commands.some(
                                        (command) =>
                                            command.type ===
                                                "SCHEDULE_SET_PICTURE_DISTANCE" ||
                                            command.type ===
                                                "SCHEDULE_MOVE_KAP",
                                    );
                                // If there is a "SCHEDULE_SET_PAYLOAD_RECORDING", ensure that
                                // either "SCHEDULE_SET_PICTURE_DISTANCE" or "SCHEDULE_MOVE_KAP" exists
                                if (
                                    hasSetPayloadRecording &&
                                    !hasSetPictureDistanceOrMoveKap
                                ) {
                                    return false; // This condition fails validation
                                }

                                return true; // Pass validation
                            },
                            {
                                message: `Command '${commandInfo.SCHEDULE_SET_PAYLOAD_RECORDING.label}' requires a picture distance but none is set.`,
                                path: [""], // Ensure the error is placed at errors.commands.message
                            },
                        ),
                    apps: z
                        .array(
                            z.object({
                                id: z.string(),
                                settings: z
                                    .object({})
                                    .catchall(z.any())
                                    .optional(),
                            }),
                        )
                        .min(1, "Select at least 1 app"),
                    deleted: z.boolean(),
                }),
            );
        }
    }, [areas]);

    // Update the form resolver once the schema is ready
    React.useEffect(() => {
        if (taskSchema) {
            reset({}, { keepValues: true, resolver: zodResolver(taskSchema) });
        }
    }, [taskSchema, reset]);

    React.useEffect(() => {
        const taskJson = location.state?.taskJson;

        if (taskJson && taskSchema && !hasFormBeenTouched) {
            setIsLoadingTask(true);
            console.log("Using imported taskJson from state:", taskJson);

            // Generate a new task id
            if (isNewTask()) {
                taskJson.id = generate();
            }

            // Populate app settings
            for (const app of taskJson.apps ?? []) {
                if (app?.settings) {
                    setExistingAppSettings((prev) => ({
                        ...prev,
                        [app.id]: app.settings,
                    }));
                }
            }

            // // Populate the form with taskJson, respecting the schema
            const schemaKeys = Object.keys(taskSchema.shape);

            // Filter taskJson to include only schema keys
            const filteredTaskJson = Object.fromEntries(
                Object.entries(taskJson).filter(([key]) =>
                    schemaKeys.includes(key),
                ),
            );

            // Reset the form with the filtered taskJson
            reset(filteredTaskJson, { keepDirty: true });

            setIsLoadingTask(false);
        }
    }, [location.state, hasFormBeenTouched, taskSchema, reset, isNewTask]);

    React.useEffect(() => {
        if (
            organizationId &&
            locationId &&
            !isNewTask() &&
            !hasFormBeenTouched &&
            !location.state?.taskJson
        ) {
            console.log("Start loading");

            setIsLoadingTask(true);

            TaskDocument(organizationId, locationId, taskId)
                .get()
                .then((task) => {
                    if (
                        !canEditTaskWithCommands(
                            task.commands,
                            getUserIsAdmin(),
                            organizationId,
                        )
                    ) {
                        navigate(
                            urls.task(organizationId, locationId, taskId),
                            {
                                message:
                                    "Task could not be edited because this task contains commands which can only be edited by admin users.",
                            },
                        );
                        return;
                    }

                    // Only reset the form when there hasn't been any user interaction
                    for (const app of task.apps ?? []) {
                        if (app?.settings) {
                            setExistingAppSettings((prev) => ({
                                ...prev,
                                [app.id]: app?.settings,
                            }));
                        }
                    }
                    reset(task);
                    console.log("Updated task being reset:", task);
                    setIsLoadingTask(false);
                });
        }
    }, [
        organizationId,
        locationId,
        taskId,
        reset,
        isNewTask,
        getUserIsAdmin,
        navigate,
        hasFormBeenTouched,
        location.state,
    ]);

    React.useEffect(() => {
        if (
            isNewTask() &&
            organizationId &&
            locationId &&
            !hasFormBeenTouched &&
            !location.state?.taskJson
        ) {
            setIsLoadingTask(true);
            reset({
                id: generate(),
                name: "",
                autoName: { enabled: true },
                commands: [commandInfo.SCHEDULE_MOVE_KAP.Skeleton],
                apps:
                    installedApps.length === 1
                        ? [{ id: installedApps[0].id, settings: {} }]
                        : [],
                deleted: false,
            });
            setIsLoadingTask(false);
        }
    }, [
        organizationId,
        locationId,
        isNewTask,
        reset,
        hasFormBeenTouched,
        installedApps,
        location.state,
    ]);

    const getAppName = React.useCallback(
        (appId) => {
            const foundApp = allApps.find((app) => app.id === appId);

            return foundApp ? foundApp.name : "Unknown app";
        },
        [allApps],
    );

    const getScanAreaName = React.useCallback((command) => {
        if (
            !command.settings.startKap &&
            !command.settings.endKap &&
            !command.settings.startLeg &&
            !command.settings.endLeg
        ) {
            return "";
        }

        if (command.settings.startKap === command.settings.endKap) {
            if (command.settings.startLeg === command.settings.endLeg) {
                return `${command.settings.startKap || ""} ${command.settings.startLeg || ""}`;
            } else {
                return `${command.settings.startKap || ""} ${command.settings.startLeg || ""}-${command.settings.endLeg || ""}`;
            }
        }

        return `${command.settings.startKap || ""} ${command.settings.startLeg || ""}-${command.settings.endKap || ""} ${command.settings.endLeg || ""}`;
    }, []);

    // Can only auto generate a name when all commands are SCHEDULE_MOVE_KAP commands
    const canUseAutoGeneratedName = watchedCommands?.every(
        (command) => command.type === "SCHEDULE_MOVE_KAP",
    );

    // Function to generate the full name based on commands and apps
    const generateAutoGeneratedName = React.useCallback(() => {
        const autoGeneratedName = canUseAutoGeneratedName
            ? watchedCommands
                  .map((command) => getScanAreaName(command))
                  .join(", ")
            : undefined;

        // Append selected app names
        if (
            autoGeneratedName !== undefined &&
            watchedApps &&
            watchedApps.length > 0
        ) {
            const appNames = watchedApps
                .map((app) => getAppName(app.id))
                .sort()
                .join(", ");

            return autoGeneratedName
                ? `${autoGeneratedName}: ${appNames}`
                : appNames;
        }

        return autoGeneratedName;
    }, [
        getScanAreaName,
        getAppName,
        watchedCommands,
        watchedApps,
        canUseAutoGeneratedName,
    ]);

    // We use these stringified commands and apps as dependency for the auto name generation
    // because else it will perform a shallow comparison and doesn't trigger the effect when
    // a command setting changes
    const watchedCommandsString = JSON.stringify(watchedCommands);
    const watchedAppsString = JSON.stringify(watchedApps);

    // biome-ignore lint/correctness/useExhaustiveDependencies: The stringified values deliberately retrigger generation for in-place nested command and app changes.
    React.useEffect(() => {
        if (watchedAutoNameEnabled) {
            const autoGeneratedName = generateAutoGeneratedName();
            if (autoGeneratedName) {
                setValue("name", autoGeneratedName);
                if (autoGeneratedName.length > 0) {
                    // Clear potential error of empty name
                    clearErrors("name");
                }
            }
        }
    }, [
        watchedCommandsString,
        watchedAppsString,
        watchedAutoNameEnabled,
        generateAutoGeneratedName,
        setValue,
        clearErrors,
    ]);

    const onDragEnd = (result) => {
        const { source, destination } = result;

        if (!destination) return;

        // Use the `move` method from useFieldArray to reorder items
        moveCommand(source.index, destination.index);
    };

    const handleAddCommand = () => {
        // Add an empty SCHEDULE_MOVE_KAP command
        const addedCommand = commandInfo.SCHEDULE_MOVE_KAP.Skeleton;
        appendCommand(addedCommand);
    };

    // Options are all apps which are installed at this location and apss which are present in the task
    const appOptions = allApps.filter(
        (app) =>
            installedApps.some((installedApp) => installedApp.id === app.id) ||
            appFields.some((field) => field.id === app.id),
    );

    // Combination of valid app options and apps which somehow came in the app fields
    const mergedApps = [
        ...appOptions,
        ...appFields.filter(
            (field) => !appOptions.some((app) => app.id === field.id),
        ),
    ];

    const onSubmit = async (taskUpdate) => {
        for (const command of taskUpdate.commands) {
            if (command.type === "SCHEDULE_FLY_TO_WAYPOINT") {
                delete command.settings.useFlyzoneYaw; // Delete the useFlyzoneYaw property
            }
        }

        // Use id from taskUpdate such that it also works for new tasks
        await TaskDocument(organizationId, locationId, taskUpdate.id).set(
            taskUpdate,
            { merge: true },
        );
        navigate(urls.task(organizationId, locationId, taskUpdate.id));
    };

    const tabName = isNewTask()
        ? "New Task"
        : `Edit Task ${nameController.field.value}`;

    return (
        <Layout>
            <title>{`${tabName} | MyCorvus`}</title>
            <Breadcrumbs
                path={breadcrumbPaths.task}
                location={locationDoc}
                task={task}
            />
            {isLoadingTask || !taskSchema ? (
                <Spinner animation="border" role="status" variant="primary">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
            ) : (
                <Form onSubmit={handleSubmit(onSubmit)}>
                    <Card className="mb-4">
                        <Card.Header className="d-flex justify-content-between">
                            <div style={{ fontWeight: "bold" }}>Name</div>
                        </Card.Header>
                        <Form.Group
                            as={Row}
                            controlId={"name"}
                            className="my-1 mx-2"
                        >
                            <Col>
                                <Form.Control
                                    type="text"
                                    placeholder={
                                        watchedAutoNameEnabled
                                            ? "Define commands and apps to show auto generated name here"
                                            : "Enter name"
                                    }
                                    isInvalid={!!errors?.name}
                                    {...nameController.field}
                                    onChange={(e) => {
                                        setValue("autoName.enabled", false); // Uncheck the auto name switch on editing
                                        nameController.field.onChange(
                                            e.target.value,
                                        );
                                    }}
                                    autoComplete="off"
                                />
                                {errors?.name && (
                                    <Form.Control.Feedback
                                        type="invalid"
                                        className="d-block"
                                    >
                                        {errors?.name?.message}
                                    </Form.Control.Feedback>
                                )}
                            </Col>
                        </Form.Group>
                        {canUseAutoGeneratedName && (
                            <Form.Group as={Row} className="my-1 mx-2">
                                <Col>
                                    <Form.Check
                                        type="switch"
                                        id="autoNameEnableSwitch"
                                        label="Use auto generated name"
                                        checked={
                                            autoNameEnabledController.field
                                                .value
                                        }
                                        onChange={(e) => {
                                            autoNameEnabledController.field.onChange(
                                                e.target.checked,
                                            );
                                            if (e.target.checked) {
                                                // Set the auto-generated name when the switch is checked
                                                setValue(
                                                    "name",
                                                    generateAutoGeneratedName(),
                                                );
                                            }
                                        }}
                                        className="mt-2"
                                    />
                                </Col>
                            </Form.Group>
                        )}
                    </Card>
                    <Card
                        className={`mb-4 ${errors?.commands ? "border-danger" : ""}`}
                    >
                        <Card.Header className="d-flex align-items-center">
                            <div
                                className="me-2"
                                style={{ fontWeight: "bold" }}
                            >
                                Commands
                            </div>
                            {errors?.commands && (
                                <div
                                    className="d-flex align-items-center text-danger"
                                    style={{ fontSize: "0.875rem" }}
                                >
                                    <BsExclamationCircle
                                        className="me-1"
                                        style={{ flexShrink: 0 }}
                                    />
                                    <div>{errors.commands.message}</div>
                                </div>
                            )}
                            {getUserIsAdmin() && (
                                <Form.Check
                                    type="switch"
                                    id="show-advanced-switch"
                                    label={
                                        <>
                                            Show{" "}
                                            <FaExclamationTriangle color="orange" />{" "}
                                            options
                                        </>
                                    }
                                    className="ms-auto"
                                    checked={showAdvancedCommandTypes}
                                    onChange={(e) =>
                                        setShowAdvancedCommandTypes(
                                            e.target.checked,
                                        )
                                    }
                                />
                            )}
                        </Card.Header>
                        <DragDropContext onDragEnd={onDragEnd}>
                            <Droppable droppableId="commands">
                                {(provided) => (
                                    <ListGroup
                                        variant="flush"
                                        {...provided.droppableProps}
                                        ref={provided.innerRef}
                                    >
                                        {commandFields.map(
                                            (command, commandIdx) => (
                                                <Draggable
                                                    key={command.id}
                                                    draggableId={command.id}
                                                    index={commandIdx}
                                                >
                                                    {(provided) => (
                                                        <div
                                                            ref={
                                                                provided.innerRef
                                                            }
                                                            {...provided.draggableProps}
                                                            style={{
                                                                ...provided
                                                                    .draggableProps
                                                                    .style,
                                                            }}
                                                        >
                                                            <ListGroup.Item
                                                                key={command.id}
                                                                className="px-3 d-flex align-items-start"
                                                            >
                                                                <div
                                                                    {...provided.dragHandleProps}
                                                                    className="mt-2 me-3"
                                                                >
                                                                    <FaGripVertical
                                                                        color="grey"
                                                                        style={{
                                                                            cursor: "grab",
                                                                            marginTop:
                                                                                "-2px",
                                                                        }}
                                                                    />
                                                                </div>

                                                                <CommandEdit
                                                                    command={
                                                                        command
                                                                    }
                                                                    commandIdx={
                                                                        commandIdx
                                                                    }
                                                                    areas={
                                                                        areas
                                                                    }
                                                                    control={
                                                                        control
                                                                    }
                                                                    watch={
                                                                        watch
                                                                    }
                                                                    clearErrors={
                                                                        clearErrors
                                                                    }
                                                                    errors={
                                                                        errors
                                                                    }
                                                                    update={
                                                                        updateCommand
                                                                    }
                                                                    userIsAdmin={getUserIsAdmin()}
                                                                    showAdvancedCommandTypes={
                                                                        showAdvancedCommandTypes
                                                                    }
                                                                    resourceOrganizationId={
                                                                        organizationId
                                                                    }
                                                                />

                                                                <div className="d-flex flex-sm-row flex-column">
                                                                    {/* This div ensures that the buttons are in a row on larger screens (sm and up),
                                                                but stack vertically on small screens. */}
                                                                    {getUserIsAdmin() && (
                                                                        <Button
                                                                            variant="outline-secondary"
                                                                            onClick={() =>
                                                                                appendCommand(
                                                                                    command,
                                                                                )
                                                                            } // Append this command at the end
                                                                            className="ms-sm-2 mb-2 mb-sm-0"
                                                                        >
                                                                            <FaClone />
                                                                        </Button>
                                                                    )}

                                                                    <Button
                                                                        variant="outline-danger"
                                                                        onClick={() =>
                                                                            removeCommand(
                                                                                commandIdx,
                                                                            )
                                                                        } // Remove the item at commandIdx
                                                                        className="ms-sm-2"
                                                                        disabled={
                                                                            commandFields.length ===
                                                                            1
                                                                        }
                                                                    >
                                                                        <FaTrash />
                                                                    </Button>
                                                                </div>
                                                            </ListGroup.Item>
                                                        </div>
                                                    )}
                                                </Draggable>
                                            ),
                                        )}
                                        {provided.placeholder}

                                        <ListGroup.Item
                                            className="px-3 d-flex align-items-center text-muted"
                                            style={{
                                                cursor: "pointer",
                                                backgroundColor: "#f8f9fa",
                                            }}
                                        >
                                            <button
                                                type="button"
                                                onClick={handleAddCommand}
                                                className="btn btn-link d-flex align-items-center text-muted p-0 w-100"
                                                style={{
                                                    textDecoration: "none",
                                                }}
                                            >
                                                <FaPlus className="me-3" />
                                                Add command
                                            </button>
                                        </ListGroup.Item>
                                    </ListGroup>
                                )}
                            </Droppable>
                        </DragDropContext>
                    </Card>
                    <Card
                        className={`mb-4 ${errors?.apps ? "border-danger" : ""}`}
                    >
                        <Card.Header className="d-flex align-items-center">
                            <div
                                className="me-2"
                                style={{ fontWeight: "bold" }}
                            >
                                Apps
                            </div>
                            {errors?.apps && (
                                <div
                                    className="d-flex align-items-center text-danger"
                                    style={{ fontSize: "0.875rem" }}
                                >
                                    <BsExclamationCircle
                                        className="me-1"
                                        style={{ flexShrink: 0 }}
                                    />
                                    <div>{errors.apps.message}</div>
                                </div>
                            )}
                        </Card.Header>
                        <ListGroup variant="flush">
                            {appOptions.length === 0 &&
                                (isLoadingApps ? (
                                    <ListGroup.Item
                                        key="loading-apps"
                                        className="px-3 d-flex align-items-start"
                                    >
                                        <Spinner
                                            animation="border"
                                            role="status"
                                            size="sm"
                                            variant="primary"
                                        >
                                            <span className="visually-hidden">
                                                Loading...
                                            </span>
                                        </Spinner>
                                    </ListGroup.Item>
                                ) : getUserIsAdmin() ? (
                                    <ListGroup.Item
                                        key="no-apps-installed-admin"
                                        className="px-3 d-flex align-items-start"
                                        variant="warning"
                                        action
                                        onClick={(e) => {
                                            e.preventDefault(); // Prevent that this click is interpreted as form submit
                                            window.open(
                                                urls.locationApps(
                                                    organizationId,
                                                    locationId,
                                                ),
                                                "_blank",
                                                "noopener,noreferrer",
                                            ); // Opens link in a new tab
                                        }}
                                    >
                                        <FaExclamationTriangle
                                            className="me-2 mt-1"
                                            style={{ marginTop: "-2px" }}
                                        />
                                        There are no apps installed in this
                                        location. Click here to install apps.
                                    </ListGroup.Item>
                                ) : (
                                    <ListGroup.Item
                                        key="no-apps-installed"
                                        variant="danger"
                                        className="px-3 d-flex align-items-start"
                                    >
                                        <FaExclamationTriangle
                                            className="me-2 mt-1"
                                            style={{ marginTop: "-2px" }}
                                        />
                                        There are no apps installed in this
                                        location. Contact Corvus for support.
                                    </ListGroup.Item>
                                ))}
                            {mergedApps.map((app) => {
                                // Check if app.id is in the list of selected apps
                                const appIndex = appFields.findIndex(
                                    (field) => field.id === app.id,
                                );
                                const isAppSelected = appIndex !== -1;

                                // Check if the app is installed
                                const isInstalled = installedApps.some(
                                    (installedApp) =>
                                        installedApp.id === app.id,
                                );

                                // Check if the app is valid (exists in appOptions)
                                const isValid = appOptions.some(
                                    (option) => option.id === app.id,
                                );

                                return (
                                    <ListGroup.Item
                                        key={app.id}
                                        className="px-3 d-flex align-items-start align-items-center"
                                    >
                                        <Form.Check
                                            type="switch"
                                            id={`switch-${app.id}`}
                                            label=""
                                            checked={isAppSelected}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    // Add the app to the apps array if the switch is turned on
                                                    const appSettings =
                                                        existingAppSettings[
                                                            app.id
                                                        ] || {};
                                                    appendApp({
                                                        id: app.id,
                                                        settings: appSettings,
                                                    });
                                                } else {
                                                    // Remove the app from the apps array if the switch is turned off
                                                    removeApp(appIndex);
                                                }
                                            }}
                                            className="me-2"
                                        />
                                        {getAppName(app.id)}
                                        {!isInstalled &&
                                            isValid &&
                                            !isLoadingApps && (
                                                <Badge
                                                    bg="warning"
                                                    className="ms-1"
                                                    size="sm"
                                                >
                                                    Not installed
                                                </Badge>
                                            )}
                                        {!isValid && !isLoadingApps && (
                                            <Badge
                                                bg="danger"
                                                className="ms-1"
                                                size="sm"
                                            >
                                                Invalid
                                            </Badge>
                                        )}
                                    </ListGroup.Item>
                                );
                            })}
                        </ListGroup>
                    </Card>

                    <Button
                        variant="success"
                        disabled={
                            isSubmitting ||
                            (!isDirty && !location.state?.taskJson)
                        }
                        type="submit"
                        className="mb-3 mt-1"
                    >
                        <FaSave className="mb-1 me-1" />
                        {isSubmitting ? "Saving..." : "Save"}
                    </Button>
                    <CancelButton
                        className="ms-2 mb-3 mt-1"
                        to={
                            isNewTask()
                                ? urls.tasks(organizationId, locationId)
                                : urls.task(organizationId, locationId, taskId)
                        }
                    />
                </Form>
            )}
        </Layout>
    );
}
